import React, { useState } from 'react';
import { Sun, Car, Home, ArrowRight, Sparkles, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Quiz from '../components/Quiz';
import emailjs from '@emailjs/browser';
import FAQs from '../components/FAQs';

const Plans = () => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const plans = [
    {
      icon: Sun,
      title: "For Homes with Solar",
      description: "Turn every unit you generate into value. Get rewarded for contributing clean energy to the network.",
      link: "/plans/solar",
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      borderColor: "border-orange-200",
      features: ["Maximize solar ROI", "Feed-in rewards", "Real-time monitoring"],
      value: "solar"
    },
    {
      icon: Car,
      title: "For Homes with EV",
      description: "Charge your EV with clean energy. Track costs and earn rewards for smart charging.",
      link: "/plans/ev",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      borderColor: "border-blue-200",
      features: ["Smart charging", "Cost tracking", "Green energy priority"],
      value: "ev"
    },
    {
      icon: Home,
      title: "Full Home Consumption",
      description: "Access clean energy without solar panels. Perfect for apartments and rental homes.",
      link: "/plans/full-home",
      color: "text-green-600",
      bgColor: "bg-green-100",
      borderColor: "border-green-200",
      features: ["No solar required", "Flexible plans", "Instant rewards"],
      value: "full-home"
    }
  ];

  const handlePlanSelect = (planValue: React.SetStateAction<string>) => {
    setSelectedPlan(planValue);
  };

  const handleSubmit = (e: { preventDefault: () => void; }) => {
    e.preventDefault();

    // Validation check
    if (!selectedPlan || !email) {
      setError('Please select a plan and enter your email address.');
      return;
    }

     
    setError('');

    // Email.js configuration
    const serviceId = 'service_11ucmf4';  // Your service ID
    const templateId = 'template_z23g6ck';  // Your template ID
    const publicKey = 'CFdP1rqvzciv4oG6c';  // Your public key

    // Prepare template parameters
    const templateParams = {
      user_email: email,
      selected_plan: selectedPlan,
      plan_name: plans.find(plan => plan.value === selectedPlan)?.title || 'Unknown Plan'
    };

    // Send email using Email.js
    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setSubmitting(false);
        setSubmitted(true);
        setEmail('');
        setSelectedPlan('');
      })
      .catch((err) => {
        console.error('FAILED...', err);
        setSubmitting(false);
        setError('Failed to send your request. Please try again later.');
      });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Sparkles className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Find Your Perfect Energy Plan
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Whether you have solar panels, an electric vehicle, or simply want cleaner energy, we have a plan tailored just for you.
            </p>
          </div>
        </div>
      </section>

      {/* Quiz Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Not sure which plan is right for you?
              </h2>
              <p className="text-lg text-gray-600">
                Take our quick quiz to get a personalized recommendation
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <Quiz />
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Available Plans
              </h2>
              <p className="text-lg text-gray-600">
                Choose the plan that best fits your home and lifestyle
              </p>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <div key={index} className="group">
                  <div
                    className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border ${selectedPlan === plan.value ? plan.borderColor + ' ring-2 ring-offset-2 ' + plan.color.replace('text', 'ring') : 'border-gray-100'} overflow-hidden h-full flex flex-col cursor-pointer`}
                    onClick={() => handlePlanSelect(plan.value)}
                  >
                    {/* Plan Header */}
                    <div className="p-8 flex-1">
                      <div className={`w-14 h-14 ${plan.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <plan.icon className={`h-7 w-7 ${plan.color}`} />
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {plan.title}
                      </h3>

                      <p className="text-gray-600 mb-6">
                        {plan.description}
                      </p>

                      {/* Features List */}
                      <ul className="space-y-3 mb-8">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center text-gray-700">
                            <Zap className={`h-4 w-4 ${plan.color} mr-3`} />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Plan Footer */}
                    <div className="px-8 pb-8">
                      <div
                        className={`inline-flex items-center justify-center w-full px-6 py-3 border-2 ${selectedPlan === plan.value ? 'bg-gray-50' : ''} ${plan.borderColor} ${plan.color} rounded-full font-medium hover:bg-gray-50 transition-all duration-300 group-hover:translate-x-1`}
                      >
                        {selectedPlan === plan.value ? 'Selected' : 'Select this plan'}
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Email Collection Form */}
            {!submitted ? (
              <div className="max-w-xl mx-auto mt-16 bg-gray-50 rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Get more information about your selected plan
                </h3>
                <p className="text-gray-600 mb-6">
                  Enter your email address and we'll send you details about {selectedPlan ? `the ${plans.find(p => p.value === selectedPlan)?.title}` : 'your selected plan'}.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className={`w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center ${(!selectedPlan || !email || submitting) ? 'opacity-50' : ''}`}
                  >
                    {submitting ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        Get Plan Details
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <div className="max-w-xl mx-auto mt-16 bg-green-50 rounded-2xl shadow-lg p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                  <Sparkles className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Thank you for your interest!
                </h3>
                <p className="text-gray-600 mb-6">
                  We've sent the details about your selected plan to your email address. Check your inbox soon!
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors duration-300"
                >
                  Select Another Plan
                </button>
              </div>
            )}

            {/* Additional CTA */}
            <div className="text-center mt-16">
              <p className="text-gray-600 mb-6">
                Need help choosing? Our energy experts are here to help.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors duration-300"
              >
                Contact an Expert
                <ArrowRight className="h-5 w-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>
      <FAQs />
      <Footer />
    </div>
  );
};

export default Plans;